import { LitElement, html, css, nothing } from 'lit'
import { customElement, state } from 'lit/decorators.js'
import { authService } from '../../services/auth/auth-service.js'
import { router } from '../../utils/router.js'
import type { MdDialog } from '@material/web/dialog/dialog.js'

import '@material/web/dialog/dialog.js'
import '@material/web/button/text-button.js'
import './auth-login.js'

@customElement('login-modal')
export class LoginModal extends LitElement {
  static override styles = css`
    .fullscreen {
      display: none;
    }

    .dialog-wrapper {
      display: contents;
    }

    @media (max-width: 768px) {
      .fullscreen {
        position: fixed;
        inset: 0;
        z-index: 2000;
        background: var(--cairn-background-color, #f8fafc);
        display: flex;
        flex-direction: column;
        overflow-y: auto;
        padding-top: env(safe-area-inset-top, 0);
        padding-bottom: env(safe-area-inset-bottom, 0);
      }

      .fullscreen auth-login {
        flex: 1;
      }

      .fullscreen-actions {
        padding: 0 var(--cairn-spacing-md, 16px) var(--cairn-spacing-xl, 32px);
        text-align: center;
      }

      .dialog-wrapper {
        display: none;
      }
    }

    md-dialog {
      --md-dialog-container-color: var(--cairn-surface-color, #ffffff);
      --md-dialog-container-shape: 16px;
      max-width: 440px;
      width: calc(100vw - 32px);
    }

    md-text-button {
      --md-text-button-label-text-color: var(--cairn-text-secondary-color, #64748b);
      --md-text-button-hover-label-text-color: var(--cairn-text-color, #1e293b);
    }
  `

  @state() private visible = false

  private unsubAuth: (() => void) | undefined
  private unsubRouter: (() => void) | undefined

  override connectedCallback(): void {
    super.connectedCallback()
    this.unsubAuth = authService.onChange(state => {
      if (state.status === 'authenticated') {
        this.close()
      }
    })
  }

  override disconnectedCallback(): void {
    super.disconnectedCallback()
    this.unsubAuth?.()
    this.unsubRouter?.()
  }

  async show(): Promise<void> {
    this.visible = true
    await this.updateComplete
    const wrapper = this.shadowRoot?.querySelector('.dialog-wrapper') as HTMLElement | null
    if (wrapper && getComputedStyle(wrapper).display !== 'none') {
      const dialog = this.dialog
      if (dialog) await dialog.show()
    }
    let first = true
    this.unsubRouter = router.subscribe(() => {
      if (first) {
        first = false
        return
      }
      this.close()
    })
  }

  private get dialog(): MdDialog | null {
    return this.shadowRoot?.querySelector('md-dialog') as MdDialog | null
  }

  private close(): void {
    if (!this.visible) return
    this.visible = false
    if (this.dialog?.open) {
      void this.dialog.close('dismiss')
    } else {
      this.handleClosed()
    }
  }

  private handleClosed(): void {
    this.unsubRouter?.()
    this.unsubRouter = undefined
    this.dispatchEvent(new Event('login-modal-dismissed'))
  }

  override render(): unknown {
    if (!this.visible) return nothing

    return html`
      <div class="fullscreen">
        <auth-login></auth-login>
        <div class="fullscreen-actions">
          <md-text-button @click=${() => this.close()}>Continue as guest</md-text-button>
        </div>
      </div>
      <div class="dialog-wrapper">
        <md-dialog @closed=${this.handleClosed}>
          <div slot="content">
            <auth-login></auth-login>
          </div>
          <div slot="actions">
            <md-text-button @click=${() => this.close()}>Continue as guest</md-text-button>
          </div>
        </md-dialog>
      </div>
    `
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'login-modal': LoginModal
  }
}
